import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { cn } from "~/lib/utils";
import { Testimonial } from "../constants";

interface TestimonialTickerProps {
  testimonials: Testimonial[];
}

export const TestimonialTicker: React.FC<TestimonialTickerProps> = ({
  testimonials,
}) => {
  const { formatMessage } = useIntl();
  const [idx, setIdx] = useState(0);
  const [fade, setFade] = useState(true);

  const goTo = useCallback(
    (i: number) => {
      setFade(false);
      setTimeout(() => {
        setIdx(i);
        setFade(true);
      }, 200);
    },
    [setFade, setIdx]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % testimonials.length);
        setFade(true);
      }, 300);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const t = testimonials[idx];

  return (
    <div
      className="mx-auto flex min-h-[120px] w-full max-w-2xl flex-col items-center justify-center text-center"
      aria-live="polite"
    >
      <div
        className="transition-opacity duration-300"
        style={{ opacity: fade ? 1 : 0 }}
      >
        <p className="text-[15px] italic leading-relaxed text-muted-foreground">
          &ldquo;
          {formatMessage({
            id: t.quoteId,
            defaultMessage: t.quoteDefault,
          })}
          &rdquo;
        </p>
        <p className="mt-3 text-xs uppercase tracking-wide text-muted-foreground/70">
          {formatMessage({
            id: t.roleId,
            defaultMessage: t.roleDefault,
          })}{" "}
          &middot;{" "}
          {formatMessage({
            id: t.orgId,
            defaultMessage: t.orgDefault,
          })}
        </p>
      </div>
      <div className="mt-4 flex gap-1.5">
        {testimonials.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Testimonial ${i + 1}`}
            className={cn(
              "h-1.5 w-1.5 rounded-full transition-colors",
              i === idx ? "bg-foreground" : "bg-muted-foreground/30"
            )}
          />
        ))}
      </div>
    </div>
  );
};
